/**
 * CustomerStats Component
 * Dashboard metrics cards for truck service overview with live data
 */

import React from 'react';
import { Grid, Skeleton, Alert, Box } from '@mui/material';
import { StatsCard } from './StatsCard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useAnalytics } from '../../hooks/useAnalytics';

export const CustomerStats: React.FC = () => {
  const { data, isLoading, error } = useAnalytics();

  // Loading skeleton
  if (isLoading) {
    return (
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {[...Array(4)].map((_, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Box sx={{ p: 2.5, border: '1px solid #e5e7eb', borderRadius: 1.5, bgcolor: '#ffffff' }}>
              <Skeleton variant="circular" width={44} height={44} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3.5 }}>
        Failed to load analytics: {error.message}
      </Alert>
    );
  }

  // No data
  if (!data) {
    return null;
  }

  const stats = [
    {
      title: '# Trucks',
      value: (data.totalTrucks ?? 0).toString(),
      trend: data.totalTrucksChange !== undefined ? {
        value: Math.abs(data.totalTrucksChange),
        isPositive: data.totalTrucksChange >= 0
      } : undefined,
      icon: <LocalShippingIcon />,
      iconColor: '#fff',
      iconBgColor: '#3b82f6',
    },
    {
      title: 'Due Within 2 Weeks',
      value: (data.dueWithin2Weeks ?? 0).toString(),
      icon: <ScheduleIcon />,
      iconColor: '#fff',
      iconBgColor: '#dc2626',
    },
    {
      title: 'Due Within 2-6 Weeks',
      value: (data.dueWithin26Weeks ?? 0).toString(),
      icon: <ScheduleIcon />,
      iconColor: '#fff',
      iconBgColor: '#f97316',
    },
    {
      title: 'Due Within 6-12 Weeks',
      value: (data.dueWithin612Weeks ?? 0).toString(),
      icon: <ScheduleIcon />,
      iconColor: '#fff',
      iconBgColor: '#10b981',
    },
  ];

  return (
    <Grid container spacing={{ xs: 2, sm: 2.5 }} sx={{ mb: { xs: 2.5, sm: 3.5 } }}>
      {stats.map((stat, index) => (
        <Grid size={{ xs: 6, sm: 6, md: 3 }} key={index}>
          <StatsCard {...stat} />
        </Grid>
      ))}
    </Grid>
  );
};

export default CustomerStats;
